import { formatStripePrice } from '@/common/utils';
import { StandardButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CurbyAccordion } from '@/components/curby-accordion';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { useCurrentUser } from '@/hooks';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { useNavigate, useParams } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useRef } from 'react';
import {
	AccordionLocation,
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { SubscriptionForm, SubscriptionFormValues } from './form';
import {
	useGetUserSubscriptionDetailsQuery,
	useStripeProductQuery,
	useUpdateUserSubscriptionDetailsMutation,
} from './graphql.generated';

export const SubscriptionDetail = () => {
	const { productId } = useParams({ from: '/_app/subscription-detail/$productId' });
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { loading, data, error } = useStripeProductQuery({ variables: { id: productId } });
	const product = data?.stripeProduct;
	const priceId = product?.default_price?.id;
	const priceAmount = formatStripePrice(product?.default_price?.unit_amount);
	const priceInterval = 'year';
	const formRef = useRef<FormikProps<SubscriptionFormValues>>(null);
	const [updateUserSubscriptionDetails] = useUpdateUserSubscriptionDetailsMutation();
	const { data: userSubscriptionDetails } = useGetUserSubscriptionDetailsQuery({
		skip: !currentUser?.id,
		variables: { id: currentUser?.id ?? '' },
	});
	const { isDesktop } = useLayoutContext();

	const initialValues = userSubscriptionDetails?.user
		? {
				isParticipatingInPrizeDraw:
					userSubscriptionDetails.user.isParticipatingInPrizeDraw ?? false,
				isOver18: userSubscriptionDetails.user.isOver18 ?? false,
			}
		: undefined;

	const handleSubmit = useCallback(
		(values: SubscriptionFormValues) => {
			if (priceId && currentUser?.id) {
				updateUserSubscriptionDetails({
					variables: {
						input: {
							id: currentUser.id,
							isOver18: values.isOver18,
							isParticipatingInPrizeDraw: values.isParticipatingInPrizeDraw,
						},
					},
				});

				navigate({ to: `/checkout-subscription/${priceId}` });
			}
		},
		[currentUser?.id, navigate, priceId, updateUserSubscriptionDetails]
	);

	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const goSubscriptionCheckoutButton = priceId ? (
		<StandardButton onClick={triggerSubmit}>Continue to Payment</StandardButton>
	) : undefined;

	const getTitle = () => {
		if (loading) {
			return 'Loading';
		}

		if (error) {
			return 'Error';
		}

		return product?.name ?? '';
	};

	return (
		<PrimaryLayout
			footer={{ children: goSubscriptionCheckoutButton }}
			header={{ backTo: '/choose-subscription', title: getTitle() }}
		>
			<ContentPanel>
				<ContentWithLoad
					loading={loading}
					error={!!error}
					isEmpty={!product}
					isEmptyMessage="No subscription selected"
					isErrorMessage="An Error occurred"
				>
					<Flex direction="column" w="100%" flex={1} align="center" gap="lg">
						<CustomContentSection
							location={ContentSectionLocation.SubscriptionDetail}
							placement={ContentSectionPlacement.Top}
						/>
						<CurbyAccordion accordionLocation={AccordionLocation.SubscriptionDetails} />
						<CustomContentSection
							location={ContentSectionLocation.SubscriptionDetail}
							placement={ContentSectionPlacement.Middle}
						/>

						{initialValues && (
							<SubscriptionForm
								onSubmit={handleSubmit}
								formRef={formRef}
								priceInterval={priceInterval}
								initialValues={initialValues}
							/>
						)}
						<Title order={2} mt="auto" fw="900">
							Total {priceAmount && priceInterval ? `${priceAmount} / ${priceInterval}` : 'N/A'}
						</Title>
						{isDesktop && goSubscriptionCheckoutButton}
						<CustomContentSection
							location={ContentSectionLocation.SubscriptionDetail}
							placement={ContentSectionPlacement.Bottom}
						/>
					</Flex>
				</ContentWithLoad>
			</ContentPanel>
		</PrimaryLayout>
	);
};
